/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import React from 'react'
import PropTypes from 'prop-types'
import { intlMessageShape } from 'sb-react-intl'
import { useUniqueId, useEffect } from 'sb-hooks'
import { constants, TrapFocus } from 'common/helpers'
import cx from 'classnames'

import CloseIcon from 'components/ui/CloseIcon/CloseIcon'
import Title from 'components/_new/typography/deprecated/Title/Title'
import Text from 'components/_new/typography/Text/Text'

import messages from './messages'
import s from './Modal.scss'


const Modal = (props) => {
  const {
    children, id, className,
    title, text, footerNode, initialFocusId, htmlRole,
    onClose,
    dataTestId,
  } = props

  const uniqueId    = useUniqueId()
  const modalId     = id || `modal_${uniqueId}`
  const trapFocusId = `trapfocus_${uniqueId}`
  const titleId     = `title_${uniqueId}`
  const textId      = `text_${uniqueId}`

  const rootClassName = cx(s.modal, className)

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const trapFocusElement  = document.getElementById(trapFocusId)
      let initialFocusElement = trapFocusElement

      if (initialFocusId) {
        initialFocusElement = document.getElementById(initialFocusId)
      }

      const trapFocus = new TrapFocus(trapFocusElement, { withInitialFocus: false })

      if (initialFocusElement) {
        initialFocusElement.focus()
      }

      trapFocus.mount()

      return () => {
        trapFocus.unmount()
      }
    }
  }, [ trapFocusId, initialFocusId ])

  return (
    <div
      id={modalId}
      className={rootClassName}
      aria-modal
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={text ? textId : undefined}
      role={htmlRole}
      data-testid={dataTestId}
    >
      <div
        id={trapFocusId}
        className={cx(s.contentContainer, s.trapFocus)}
        tabIndex={0}
      >
        <div className={s.content}>
          {
            Boolean(title) && (
              <Title size="lg" message={title} html id={titleId} />
            )
          }
          {
            Boolean(text) && (
              <Text message={text} html muted id={textId} />
            )
          }
          {children}
        </div>
        {footerNode}
        {
          typeof onClose === 'function' && (
            <CloseIcon
              className={s.closeIcon}
              ariaLabel={messages.close}
              dataTestId={constants.testIds.modal.closeIcon}
              onClick={onClose}
            />
          )
        }
      </div>
    </div>
  )
}

Modal.propTypes = {
  id: PropTypes.string,
  title: PropTypes.oneOfType([
    PropTypes.shape(intlMessageShape),
    PropTypes.string,
  ]),
  text: PropTypes.oneOfType([
    PropTypes.shape(intlMessageShape),
    PropTypes.string,
  ]),
  footerNode: PropTypes.node,
  initialFocusId: PropTypes.string,
  onClose: PropTypes.func,
  className: PropTypes.string,
  htmlRole: PropTypes.string,
}

Modal.defaultProps = {
  htmlRole: 'dialog',
}


export default Modal
